import React, { forwardRef, ReactNode, useMemo, useState } from "react";
import Icon from "../Icon/Icon";
import { usePagination } from "utils/hooks/usePagination";
import { table } from "console";
import { useNavigate, } from "react-router";
import EntryFormatter from "utils/EntryFormatter";
import InputField from "../InputField/InputField";
import Button from "../Button/Button";
import { TableFooterProps } from "@mui/material";
import { Link, LinkProps, useSearchParams } from "react-router-dom";
interface TableFooter {
    totalItems: number
    totalPages?: number
    isFirstPage?: boolean
    isLastPage?: boolean
}
interface DefaultTableProps {
    children: ReactNode;
    className?: string;
    ref?: any
}
interface TableHeaderFilterProps {
    isSearchable?: boolean
    isLoading?: boolean
    totalItems: number
    totalSelected?: number
    handleExport?: () => void
    customButton?: JSX.Element
}
interface TableCellProps {
    children: ReactNode;
    isHeader?: boolean;
    className?: string;
    classNameComponent?: string;
    link?: LinkProps;
    colspan?: number;
}
const TableHeaderFilter: React.FC<TableHeaderFilterProps> = ({ handleExport, totalItems, totalSelected, customButton, isLoading }) => {
    const navigate = useNavigate()
    const [params, setParams] = useSearchParams()
    const [value, setValue] = useState(() => params.get('search') || '')

    return <header className='flex items-center justify-between w-full text-sm text-gray-400 p-3 gap-3'>
        <form className='flex gap-2 items-center'
            onSubmit={(event: any) => {
                event.preventDefault();
                const value = event.currentTarget.elements[0]?.value
                setParams(params => {
                    const parameters = new URLSearchParams(params)
                    parameters.set('search', value)
                    return parameters
                })
            }}>
            <InputField placeholder='Search People' size='tiny' value={value} onChange={(e: any) => setValue(e.target.value)} />
            <Button iconName='search' iconSize={16} disabled={isLoading} size='xs' action="submit" isProcessing={isLoading} >Search</Button>
        </form>
        <div className="flex flex-row items-center ml-auto gap-5">
            <p className='text-gray-400 sm:block hidden'>
                <b className='text-white-500 mr-1'>{EntryFormatter.formatNumber(totalSelected || totalItems)}</b>
                {' '}results found</p>
            {handleExport && <Button onClick={() => navigate('/exports')} iconName='document' size='xs'>Export Lists</Button>}
            {customButton}
        </div>
    </header>
}
const TableHeader: React.FC<DefaultTableProps> = ({ children, className }) => {
    return <thead className={className}>{children}</thead>;
};
const TableFooter: React.FC<TableFooter> = ({ totalItems, }) => {
    const { showing, isFirstPage, isLastPage, handleGoNextPage, handleGoLastPage, handleGoFirstPage, handleGoPreviousPage } = usePagination({ totalItems })
    return (
        <div className='flex items-center h-fit justify-between w-full p-5 px-7 border-t border-gray-700'>
            <div className='flex items-center text-gray-400 gap-1' style={{
                fontFamily: 'Inter', fontWeight: '200',
                fontSize: 13
            }}>
                Showing
                <div>
                    <strong className='font-bold text-white-500'>{showing > totalItems ? totalItems : showing}</strong>
                </div>
                of
                <strong className='font-bold text-white-500'>{totalItems}</strong>
            </div>
            <div className='flex sm:flex-row gap-4 sm:gap-6 justify-end'>
                <div className='pagination'>
                    <button
                        className={isFirstPage ? 'disabled' : 'hover:!bg-paper-100 text-white-500'}
                        onClick={handleGoFirstPage}
                    >
                        <Icon name='angle-left-double' size={16} />
                    </button>
                    <button
                        className={isFirstPage ? 'disabled' : 'hover:!bg-paper-100 text-white-500'}
                        onClick={handleGoPreviousPage}
                    >
                        <Icon name='angle-left' size={16} />
                    </button>
                    <button
                        className={isLastPage ? 'disabled' : 'hover:!bg-paper-100 text-white-500'}
                        onClick={handleGoNextPage}
                    >
                        <Icon name='angle-right' size={16} />
                    </button>
                    <button
                        className={isLastPage ? 'disabled' : 'hover:!bg-paper-100 text-white-500'}
                        onClick={handleGoLastPage}
                    >
                        <Icon name='angle-right-double' size={16} />
                    </button>
                </div>
            </div>
        </div>
    )
};
const TableBody: React.FC<DefaultTableProps> = ({ children, className }) => {
    return <tbody className={className}>{children}</tbody>;
};
const TableRow = forwardRef<HTMLTableRowElement, DefaultTableProps>(
    ({ children, className }, ref) => {
        return (
            <tr ref={ref} className={className}>
                {children}
            </tr>
        );
    }
);
const TableCell: React.FC<TableCellProps> = ({
    children,
    isHeader = false,
    classNameComponent,
    className,
    link,
    colspan = 1,
}) => {
    const CellTag = isHeader ? "th" : "td";
    if (link) {
        return <CellTag colSpan={colspan} className={` border-t hover:bg-gray-700 cursor-pointer border-gray-700 text-white-500  h-[59px] text-sm ${CellTag === 'th' && `!text-gray-400 !font-normal p-2`} ${className} `}>
            <div className={`flex truncate items-center justify-start text-center text-sm h-[59px] max-h-[59px] w-full ${classNameComponent || ''}`}>
                <Link {...link} replace={false} className={`h-fit  p-4  justify-start items-center truncate w-full text-start align-middle block ${className}`}>
                    {children}
                </Link>
            </div>
        </CellTag>
    }
    return <CellTag className={` ${className} p-4 border-t border-gray-700 text-white-500 text-sm ${CellTag === 'th' && `!text-gray-400 !font-normal`}`}>
        {children}
    </CellTag>;
};
const Table: React.FC<DefaultTableProps> = ({ children }) => {
    return <table className='table-auto h-[59px] w-full bg-gray-800 rounded-lg overflow-hidden'>
        {children}
    </table>
};

export { TableHeader, TableBody, TableRow, TableCell, Table, TableHeaderFilter, TableFooter };